import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { colors } from '@tempmee/tailwind';

import { remoteConfigReady } from '~/api/firebaseConfig';
import { SaltclickChatbot } from '~/constants/ff.posthog';
import {
  buildSaltclickChatIdentity,
  endSaltclickChatSession,
  fetchSaltclickIdentityToken,
  getSaltclickVerifiedForUserId,
  ISaltclickChatIdentity,
  ISaltclickIdentityTokenResponse,
  setSaltclickVerifiedForUserId,
  toSaltclickPrechatFields,
} from '~/domain/SaltclickChat';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireHelpChatbotButtonTapped } from '~/posthogs/events';
import { routes } from '~/routes';
import { TRootState } from '~/store/types';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

const SCRIPT_ID = 'saltclick-bootstrap';
// Salesforce can fail silently (blocked script, CORS on its own subrequests),
// so the skeleton gets a deadline instead of pulsing forever.
const SKELETON_TIMEOUT_MS = 20000;
const STYLE_ID = 'saltclick-visibility';
const VISIBLE_CLASS = 'saltclick-visible';
const READY_CLASS = 'saltclick-ready';

/**
 * Salesforce Embedded Messaging renders everything (button + chat) into a
 * `#embedded-messaging` container and can only be init'd once per page load,
 * so visibility is driven by a body class instead of unloading the widget.
 * The widget renders async after init; the CSS rule keeps it hidden on any
 * route where the Help page isn't mounted, even if init finishes after the
 * user has navigated away.
 *
 * The `::after` skeleton stands in for the launcher until the widget paints —
 * only on Help (`VISIBLE_CLASS`), and only before ready. A landing straight on
 * /help still waits on Salesforce, so the corner shows a placeholder rather
 * than nothing. ponytail: a pseudo-element keeps this out of the React tree;
 * promote it to a real component if it ever needs interaction.
 */
const ensureVisibilityStyle = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    body:not(.${VISIBLE_CLASS}) #embedded-messaging { display: none !important; }
    body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after {
      content: '';
      position: fixed;
      bottom: 1.25rem;
      right: 1.25rem;
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 9999px;
      background: ${colors.neutral[200]};
      animation: saltclick-skeleton-pulse 1.5s ease-in-out infinite;
    }
    @keyframes saltclick-skeleton-pulse { 50% { opacity: 0.4; } }
    @media (prefers-reduced-motion: reduce) {
      body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after { animation: none; }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Embeds the Saltclick (Salesforce Embedded Messaging) chatbot. Called once
 * from the authenticated layout so the widget boots in the background right
 * after login and the launcher is already painted (just hidden) by the time
 * the user opens Help — no load-then-pop. Gated by the
 * `GOTU-4448-saltclick-chatbot` PostHog flag. GOTU-5243.
 *
 * The session is verified rather than anonymous: auth-service mints a short-lived
 * JWT for the logged-in account and the widget is told to trust it, which is what
 * separates one user's conversation from the next in a shared tab. GOTU-5918.
 */
export const useSaltclickChat = (): void => {
  const flagEnabled = useFeatureFlag(SaltclickChatbot);
  // The office being acted as — under an office switch, the selected child.
  // This is what pre-chat reports, since it is who the chat is about.
  const user = useUserSelector();
  const officeId: string | undefined = user?.id;
  // The account the access token belongs to, which is who auth-service mints for
  // and therefore what a verified session is keyed on. Same as `officeId` unless
  // a parent is acting as one of its child offices.
  const accountId = useSelector((state: TRootState) => state.user.user?.id);
  // Logged-in users only — never show the widget when Help is reached from
  // login/sign-up (GOTU-5243).
  const enabled = flagEnabled && !!user;
  const isHelpRoute = useLocation().pathname === routes.help;

  const [isMessagingReady, setIsMessagingReady] = useState(false);

  // Read from event handlers (ready, token expiry) that outlive the render they
  // were registered in, so they need the current user, not a captured one.
  const identityRef = useRef<ISaltclickChatIdentity | null>(null);
  const accountIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    identityRef.current = buildSaltclickChatIdentity(user);
    accountIdRef.current = accountId;
  }, [accountId, user]);

  /** The account a token request is currently out for, if any. */
  const requestedForRef = useRef<string | null>(null);
  /** The most recently minted token, and the account it belongs to. */
  const tokenRef = useRef<ISaltclickIdentityTokenResponse | null>(null);
  const mintedForRef = useRef<string | null>(null);

  /**
   * Mints a token for whoever is logged in now and holds it.
   *
   * Deliberately independent of the widget: the mint only needs an account and an
   * access token, so it runs as soon as the flag is on — in parallel with the
   * bootstrap script rather than after it. That keeps the endpoint observable when
   * the widget cannot accept a token at all (User Verification switched off on the
   * Salesforce deployment), and shortens the window where a conversation could
   * start before its token arrived.
   */
  const mintIdentityToken = useCallback(async (): Promise<void> => {
    const accountAtRequest = accountIdRef.current;
    if (!accountAtRequest) return;

    // One request in flight per account: this runs from the mount, from the ready
    // event, and from token expiry, which can otherwise overlap.
    if (requestedForRef.current === accountAtRequest) return;
    requestedForRef.current = accountAtRequest;

    try {
      // No argument: auth-service mints for whoever the access token belongs to,
      // so the token's subject is the answer, not something we ask for.
      const identityToken = await fetchSaltclickIdentityToken();

      // A token that arrives after the account changed belongs to nobody we can
      // use it for, so it is dropped rather than held.
      if (accountIdRef.current !== accountAtRequest) return;

      // Belt and braces on the one thing that must not go wrong: the token has
      // to be for the account we think is logged in. Anything else would file
      // the conversation under someone else.
      if (identityToken.sub && identityToken.sub !== accountAtRequest) {
        console.error(
          `[Saltclick] identity token was minted for ${identityToken.sub}, ` +
            `not ${accountAtRequest} — leaving the chat session anonymous`,
        );

        return;
      }

      tokenRef.current = identityToken;
      mintedForRef.current = accountAtRequest;
    } catch (err) {
      // Leaves the session anonymous — the behaviour before verification
      // existed — rather than taking the whole widget down. Includes the 503 an
      // environment returns until its signing key is provisioned, and the 400 for
      // an account whose user type Salesforce has no mapping for.
      console.error('[Saltclick] Could not mint a chat identity token: ', err);
    } finally {
      // Only clear the slot this call owns; a newer request for a different
      // account has already claimed it.
      if (requestedForRef.current === accountAtRequest) requestedForRef.current = null;
    }
  }, []);

  /**
   * Hands the held token to the widget, if there is one and the widget can take
   * it. A no-op until both are true, and safe to retry — the widget accepts a new
   * token at any point.
   */
  const applyHeldToken = useCallback((): void => {
    const account = accountIdRef.current;
    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    const identityToken = tokenRef.current;
    if (!account || !verificationApi || !identityToken || mintedForRef.current !== account) return;
    // Already verified as this account: a late-settling mint for a previous one
    // would otherwise re-hand the same token.
    if (getSaltclickVerifiedForUserId() === account) return;

    verificationApi.setIdentityToken({
      identityTokenType: identityToken.tokenType ?? 'JWT',
      identityToken: identityToken.token,
    });
    setSaltclickVerifiedForUserId(account);
    // Logged because the failure this replaced was silent: an anonymous session
    // looks identical to a verified one from the outside, so "did the token
    // land?" was otherwise unanswerable from a support session. The token itself
    // is never logged — it is a bearer credential; `kid` and `expiresIn` identify
    // it without carrying anything sensitive.
    console.log(
      `[Saltclick] Verified chat session for ${account} (kid ${identityToken.kid ?? 'unknown'}, ` +
        `expires in ${identityToken.expiresIn ?? '?'}s)`,
    );
  }, []);

  /** Mint if we do not already hold a usable token, then hand it over. */
  const verifySession = useCallback(async (): Promise<void> => {
    if (mintedForRef.current !== accountIdRef.current) await mintIdentityToken();
    applyHeldToken();
  }, [applyHeldToken, mintIdentityToken]);

  useEffect(() => {
    if (!enabled) return undefined;

    ensureVisibilityStyle();
    window.addEventListener('onEmbeddedMessagingConversationOpened', fireHelpChatbotButtonTapped);

    // The class swaps the skeleton for the real launcher (or drops it when the
    // widget never arrives). It lives on the body, not in state, so it survives
    // the hook remounting on a fresh login.
    const hideSkeleton = () => document.body.classList.add(READY_CLASS);
    const onReady = () => {
      hideSkeleton();
      setIsMessagingReady(true);
      // `userVerificationAPI` only exists when User Verification is enabled on the
      // Salesforce deployment. Without it there is nothing to hand a token to, so
      // say so once — otherwise the session is quietly anonymous and the missing
      // token request looks like a bug on this side.
      if (!window.embeddedservice_bootstrap?.userVerificationAPI) {
        console.error(
          '[Saltclick] Widget is ready but exposes no userVerificationAPI — User ' +
            'Verification is not enabled on this Salesforce deployment, so the chat ' +
            'session stays anonymous',
        );
      }
    };
    window.addEventListener('onEmbeddedMessagingReady', onReady);
    const skeletonTimer = window.setTimeout(hideSkeleton, SKELETON_TIMEOUT_MS);

    // The widget asks for a token whenever the one it holds stops being usable —
    // expiry, or the reset that follows clearSession.
    const onIdentityTokenExpired = () => {
      // The held token is spent, so the next call has to mint a fresh one — and
      // the session no longer counts as verified until that one is handed over.
      mintedForRef.current = null;
      setSaltclickVerifiedForUserId(undefined);
      void verifySession();
    };
    window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', onIdentityTokenExpired);

    let cancelled = false;
    // Remote config is fetched fire-and-forget at startup, so on a first login
    // the values aren't activated yet — wait for them instead of bailing out.
    void remoteConfigReady.then(() => {
      if (cancelled || document.getElementById(SCRIPT_ID)) return;

      // Same SALTCLICK_CHAT_CONFIG remote-config key as mobile; fails closed
      // (no bundled default) when unset or incomplete.
      const { orgId, deploymentName, siteUrl, scrt2Url } =
        RemoteConfigUtils.getSaltclickChatConfig() ?? {};
      if (!orgId || !deploymentName || !siteUrl || !scrt2Url) {
        console.error('[Saltclick] Missing or incomplete SALTCLICK_CHAT_CONFIG remote config');
        hideSkeleton();
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${siteUrl}/assets/js/bootstrap.min.js`;
      script.onerror = () => {
        console.error('[Saltclick] Failed to load bootstrap script');
        hideSkeleton();
      };
      script.onload = () => {
        const bootstrap = window.embeddedservice_bootstrap;
        if (!bootstrap) {
          console.error('[Saltclick] embeddedservice_bootstrap not available after script load');
          hideSkeleton();
          return;
        }
        try {
          bootstrap.settings.language = 'en_US';
          bootstrap.init(orgId, deploymentName, siteUrl, { scrt2URL: scrt2Url });
        } catch (err) {
          console.error('Error loading Embedded Messaging: ', err);
          hideSkeleton();
        }
      };
      document.body.appendChild(script);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(skeletonTimer);
      window.removeEventListener(
        'onEmbeddedMessagingConversationOpened',
        fireHelpChatbotButtonTapped,
      );
      window.removeEventListener('onEmbeddedMessagingReady', onReady);
      window.removeEventListener(
        'onEmbeddedMessagingIdentityTokenExpired',
        onIdentityTokenExpired,
      );
    };
  }, [enabled, verifySession]);

  // Verified identity. `userVerificationAPI` only exists once messaging is ready,
  // so it is the real gate — `isMessagingReady` is just the signal to re-run.
  // Keyed on the account rather than the acting office: switching between one's
  // own offices is the same person and must not restart the conversation, while a
  // different login in the same tab must. Also covers a fresh login where the
  // already-init'd widget never fires the ready event again.
  useEffect(() => {
    if (!enabled || !accountId) return;

    if (getSaltclickVerifiedForUserId() === accountId) return;

    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    if (verificationApi && getSaltclickVerifiedForUserId()) {
      // A session verified as someone else has to be ended before this account
      // can take over — the fallback for a handover logout did not get to clean
      // up after (a crashed tab, or a session dropped without LOGOUT_ACTION).
      void endSaltclickChatSession().then(verifySession);

      return;
    }

    void verifySession();
  }, [accountId, enabled, isMessagingReady, verifySession]);

  // Pre-chat context: the same identity the token is minted from, so the bot and
  // the agent know who they are talking to without asking. `gtAdminPanelId` is
  // the field Salesforce keys session control off. Re-sends when the user changes
  // (office switch, or a fresh login in the same tab).
  useEffect(() => {
    if (!officeId) return;

    const identity = identityRef.current;
    if (!identity) return;

    const prechatApi = window.embeddedservice_bootstrap?.prechatAPI;
    if (!prechatApi) {
      // Before ready the widget simply does not exist yet, and `isMessagingReady`
      // brings this effect back — so only a *ready* widget without `prechatAPI` is
      // an anomaly. Reported for the same reason as the missing `userVerificationAPI`
      // above: the conversation would otherwise reach an agent with no office on it
      // and nothing would say why.
      if (isMessagingReady) {
        console.error(
          '[Saltclick] Widget is ready but exposes no prechatAPI — no pre-chat ' +
            'form is configured on this Salesforce deployment, so the conversation ' +
            'carries no gtAdminPanelId',
        );
      }

      return;
    }

    const fields = toSaltclickPrechatFields(identity);
    prechatApi.setHiddenPrechatFields(fields);
    // Field *names* only: the identity holds PII (`email`) that this must never
    // put in a console. Which keys went is the useful part anyway — Salesforce
    // discards undeclared fields silently, so `DECLARED_PRECHAT_FIELDS` is what
    // this line lets you confirm against their config.
    console.log(
      `[Saltclick] Sent hidden pre-chat fields for ${identity.gtAdminPanelId}: ` +
        `${Object.keys(fields).join(', ')}`,
    );
  }, [isMessagingReady, officeId]);

  useEffect(() => {
    if (!enabled || !isHelpRoute) return undefined;
    document.body.classList.add(VISIBLE_CLASS);
    return () => document.body.classList.remove(VISIBLE_CLASS);
  }, [enabled, isHelpRoute]);
};
